import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDevice } from '@/contexts/DeviceContext';
import { Header } from '@/components/Header';
import { FlowNavigation } from '@/components/FlowNavigation';
import { TimeOptionGrid, type TimeOptionItem } from '@/components/TimeOptionGrid';
import './DinnerReservation.scss';

interface DinnerTime {
    dinnerTimeId: number;
    dinnerTime: string;
    isPast: boolean;
}

export function DinnerReservation() {
    const navigate = useNavigate();
    const { isMobile } = useDevice();

    const [dinnerTimes, setDinnerTimes] = useState<DinnerTime[]>([]);
    const [selectedDinnerTimeId, setSelectedDinnerTimeId] = useState<number | null>(null);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        fetch('/api/center/dinners')
            .then((res) => {
                if (!res.ok) throw new Error('Failed to fetch dinners');
                return res.json();
            })
            .then((data: DinnerTime[]) => {
                setDinnerTimes(data);
            })
            .catch((error) => {
                console.error('Error fetching dinners:', error);
            })
            .finally(() => {
                setIsLoading(false);
            });
    }, []);

    const handleNext = async () => {
        if (!selectedDinnerTimeId) {
            return;
        }

        try {
            const response = await fetch('/api/center/dinners/reservations', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    dinnerTimeId: selectedDinnerTimeId,
                }),
            });

            if (response.ok) {
                const data = await response.json();
                navigate('/dinner/complete', {
                    state: {
                        reservationData: data,
                    },
                });
                return;
            }

            const errorData = await response.json().catch(() => ({}));
            alert(errorData.message || '신청에 실패했습니다.');
        } catch (error) {
            console.error('Dinner reservation failed:', error);
            alert('신청 중 오류가 발생했습니다.');
        }
    };

    const timeOptions: TimeOptionItem[] = dinnerTimes.map((slot) => ({
        id: slot.dinnerTimeId,
        disabled: slot.isPast,
        content: slot.dinnerTime.substring(0, 5),
    }));

    if (isLoading) {
        return <div className="flex justify-center items-center h-screen">Loading dinners...</div>;
    }

    return (
        <div className={`dinner-reservation-page ${isMobile ? 'dinner-reservation-page--mobile' : ''}`}>
            <Header />

            <main className="dinner-reservation-main">
                <div className="dinner-main-top">
                    <h1 className="page-title">석식 신청</h1>
                    <p className="page-subtitle">원하는 석식 시간을 선택해주세요</p>
                </div>

                <div className="dinner-time-selection-container">
                    <TimeOptionGrid
                        items={timeOptions}
                        selectedId={selectedDinnerTimeId}
                        onSelect={setSelectedDinnerTimeId}
                        emptyText="신청 가능한 석식 시간이 없습니다."
                    />
                </div>
            </main>

            <FlowNavigation
                onPrev={() => navigate('/selection')}
                onNext={() => void handleNext()}
                nextLabel="신청"
                isNextDisabled={!selectedDinnerTimeId}
                hideNextArrow
            />
        </div>
    );
}
